// Keep in sync with web/src/services/attribution/browserAttribution.js.
// Shared wire contract: POST /v1/attribution/click, opaque tracking_id handoff.
(() => {
  if (window.__adAttribution) { return; }
  const KEY = 'coparenting.ad-attribution.v1';
  const TTL = 90 * 24 * 60 * 60 * 1000;
  const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  const clicks = ['oppref', 'gclid', 'gbraid', 'wbraid', 'fbclid'];
  const utms = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
  const hosts = ['co-parenting.app', 'www.co-parenting.app', 'varannanvecka.app', 'www.varannanvecka.app'];
  let record;
  let pending;
  let generation = 0;
  const permitted = () => {
    if (window.cordova) { return false; }
    const consent = window.__siteConsent && window.__siteConsent.read();
    if (consent && consent.marketing === false) { return false; }
    return !window.__siteConsent || window.__adAttributionAllowUndecided === true
      || Boolean(consent && consent.marketing === true);
  };
  const save = () => {
    try {
      if (record) { localStorage.setItem(KEY, JSON.stringify(record)); }
      else { localStorage.removeItem(KEY); }
    } catch (_) { /* Continue in memory when browser storage is disabled. */ }
  };
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (saved && ID.test(saved.trackingId) && saved.expiresAt > Date.now()) { record = saved; }
  } catch (_) { /* No persisted attribution. */ }
  const uuid = () => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] % 16) + 64;
    bytes[8] = (bytes[8] % 64) + 128;
    const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
  const usable = () => permitted() && record && record.confirmed && record.expiresAt > Date.now();
  const eligible = url => url.protocol === 'https:' && (
    (hosts.includes(url.hostname) && !url.port)
    || (url.hostname === 'play.google.com' && url.pathname === '/store/apps/details'
      && url.searchParams.get('id') === 'app.varannanvecka'));
  const decorate = value => {
    const url = new URL(value, location.href);
    if (!eligible(url)) { return value; }
    if (url.hostname === 'play.google.com') {
      const ref = new URLSearchParams(url.searchParams.get('referrer') || '');
      ref.delete('tracking_id');
      if (usable()) {
        ref.set('tracking_id', record.trackingId);
        utms.forEach(key => { if (record.references && record.references[key]) { ref.set(key, record.references[key]); } });
      }
      if (ref.toString()) { url.searchParams.set('referrer', ref.toString()); }
      else { url.searchParams.delete('referrer'); }
    } else {
      // Same-host navigation already has storage; only decorate the app/store handoff.
      if (url.hostname === location.hostname && !/^\/(get|install|signup)(\/|$)/.test(url.pathname)) { return value; }
      url.searchParams.delete('tracking_id');
      if (usable() || !permitted()) { clicks.forEach(key => url.searchParams.delete(key)); }
      if (usable()) { url.searchParams.set('tracking_id', record.trackingId); }
    }
    return url.href;
  };
  const links = () => document.querySelectorAll('a[href]').forEach(link => {
    try { link.href = decorate(link.href); } catch (_) { /* Non-URL links. */ }
  });
  const refresh = () => {
    if (!permitted()) {
      generation++;
      record = undefined;
      pending = undefined;
      save();
      links();
      return Promise.resolve();
    }
    if (pending) { return pending; }
    if (record && record.expiresAt <= Date.now()) { record = undefined; save(); }
    const params = new URLSearchParams(location.search);
    const refs = {};
    clicks.concat(utms).forEach(key => {
      const value = params.get(key);
      // Same rule as the backend: not blank, bounded, no C0/C1 control characters.
      if (value && value.trim() && value.length <= (clicks.includes(key) ? 2048 : 256)
          && !/[\u0000-\u001f\u007f-\u009f]/.test(value)) { refs[key] = value; }
    });
    const hasClick = clicks.some(key => refs[key]);
    const fingerprint = JSON.stringify(refs);
    if (hasClick && (!record || record.fingerprint !== fingerprint)) {
      try {
        record = { trackingId: uuid(), previousTrackingId: usable() ? record.trackingId : undefined,
          references: refs, fingerprint, expiresAt: Date.now() + TTL, confirmed: false };
        save();
      } catch (_) { return Promise.resolve(); }
    } else if (!hasClick && ID.test(params.get('tracking_id') || '')
        && (!record || record.trackingId !== params.get('tracking_id'))) {
      record = { trackingId: params.get('tracking_id'), expiresAt: Date.now() + TTL, confirmed: true };
      save();
    }
    if (!record || record.confirmed) { links(); return Promise.resolve(); }
    const attempt = record;
    const revision = generation;
    pending = new Promise(resolve => {
      let finished = false;
      const finish = () => {
        if (finished) { return; }
        finished = true;
        clearTimeout(timeout);
        pending = undefined;
        links();
        resolve();
      };
      const timeout = setTimeout(finish, 2000);
      Promise.resolve().then(() => fetch('https://api.varannanvecka.app/v1/attribution/click', {
        method: 'POST', credentials: 'omit', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trackingId: attempt.trackingId, previousTrackingId: attempt.previousTrackingId,
          references: attempt.references }),
      })).then(response => response.ok ? response.json() : null).then(result => {
        if (revision === generation && permitted() && record === attempt && result
            && result.trackingId === attempt.trackingId && Number.isFinite(result.expiresAt) && result.expiresAt > Date.now()) {
          record.confirmed = true;
          record.expiresAt = Math.min(record.expiresAt, result.expiresAt);
          save();
        }
      }).catch(() => { /* Keep this mapping pending for a later retry. */ }).then(finish);
    });
    return pending;
  };
  window.__adAttribution = {
    refresh, decorate,
    async destination(url) { await refresh(); return decorate(url); },
    async getTrackingId() { await refresh(); return usable() ? record.trackingId : undefined; },
  };
  document.addEventListener('DOMContentLoaded', links);
  document.addEventListener('click', event => {
    const link = event.target && event.target.closest && event.target.closest('a[href]');
    if (!link || event.defaultPrevented || link.hasAttribute('download')) { return; }
    let url;
    try { url = new URL(link.href); } catch (_) { return; }
    if (!eligible(url)) { return; }
    link.href = decorate(link.href);
    if (!record || record.confirmed || !permitted()) { return; }
    // Wait for the mapping, including an immediate tap while the capture is in flight.
    event.preventDefault();
    const newTab = event.ctrlKey || event.metaKey || event.shiftKey || link.target === '_blank';
    const opened = newTab ? window.open('about:blank', '_blank') : null;
    if (opened) { opened.opener = null; }
    window.__adAttribution.destination(link.href).then(target => {
      if (opened) { opened.location.replace(target); }
      else if (!newTab) { location.assign(target); }
    });
  });
  refresh().catch(() => { /* Attribution must not prevent the page from loading. */ });
})();



// WEBPACK FOOTER //
// ./src/services/attribution/browserAttribution.js